import { useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  Box,
  CircularProgress,
  Alert,
  Pagination,
} from "@mui/material";
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  AccountBalanceWallet as CreditIcon,
} from "@mui/icons-material";
import { useGetAuditTrailAsUser } from "@taxassist/api-client";
import type {
  FirmResponse,
  GetAuditTrailAsUser200RecordsItem,
} from "@taxassist/api-client";

interface CreditHistoryProps {
  firm: FirmResponse;
  limit?: number;
  showPagination?: boolean;
}

interface CreditTransaction {
  auditId: string;
  timestamp: string;
  userEmail: string;
  userRole: string;
  eventType:
    | "CREDITS_ADDED"
    | "CREDITS_USED"
    | "CREDITS_EXPIRED"
    | "CREDITS_REFUNDED";
  credits: number;
  invoiceId?: string;
  reason?: string; // Legacy field for backward compatibility
  batchId?: string;
  jobId?: string;
  expiryDate?: string;
  success: boolean;
  errorMessage?: string;
}

/**
 * CreditHistory displays credit-related transactions from the audit trail.
 * Uses specific audit event types (CREDITS_ADDED, CREDITS_USED, CREDITS_EXPIRED, CREDITS_REFUNDED)
 * provided by the backend for accurate and robust credit history tracking.
 */
export function CreditHistory({
  firm,
  limit = 10,
  showPagination = true,
}: CreditHistoryProps) {
  // Fetch audit trail data with optimized server-side filtering
  const {
    data: auditData,
    isLoading,
    error,
  } = useGetAuditTrailAsUser(
    {
      firmId: firm.firmId,
      resourceType: "FIRM", // Server-side filtering for FIRM resources only
      eventTypes: "CREDITS_ADDED,CREDITS_USED,CREDITS_EXPIRED,CREDITS_REFUNDED", // Server-side filtering for credit events
      limit: limit,
    },
    {
      query: {
        enabled: !!firm.firmId,
        staleTime: 5 * 60 * 1000, // 5 minutes
        queryKey: ["audit-trail", firm.firmId, "credit-events", limit], // Updated cache key for credit-specific events
      },
    },
  );

  // Parse audit records - optimized with server-side filtering
  const creditTransactions = useMemo((): CreditTransaction[] => {
    // Handle different response formats - we fixed the API to return 'records'
    const records = auditData?.records || [];

    if (!records.length) return [];

    return records
      .map((record: GetAuditTrailAsUser200RecordsItem): CreditTransaction => {
        const metadata = record.metadata || {};

        // Extract event type from the record directly (now available at top level)
        const eventType = (record.eventType || String(metadata.eventType)) as
          | "CREDITS_ADDED"
          | "CREDITS_USED"
          | "CREDITS_EXPIRED"
          | "CREDITS_REFUNDED";
        const additionalData = (metadata.additionalData || metadata) as Record<
          string,
          any
        >;

        // Extract credit amount based on event type
        let credits = 0;
        switch (eventType) {
          case "CREDITS_ADDED":
            credits = Number(additionalData.credits || 0);
            break;
          case "CREDITS_USED":
            credits = Number(additionalData.creditsUsed || 0);
            break;
          case "CREDITS_EXPIRED":
            credits = Number(additionalData.creditsExpired || 0);
            break;
          case "CREDITS_REFUNDED":
            credits = Number(additionalData.creditsRefunded || 0);
            break;
        }

        return {
          auditId: record.auditId,
          timestamp: record.timestamp,
          userEmail: record.userEmail,
          userRole: record.userRole,
          eventType,
          credits,
          invoiceId: String(additionalData.invoiceId || ""),
          reason: String(additionalData.reason || ""), // Keep for backward compatibility with old audit logs
          batchId: String(additionalData.batchId || ""),
          jobId: String(additionalData.jobId || ""),
          expiryDate: String(additionalData.expiryDate || ""),
          success: record.success,
          errorMessage: record.errorMessage,
        };
      })
      .sort(
        (a, b) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
      );
  }, [auditData]);

  const formatDate = (timestamp: string) => {
    return new Date(timestamp).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getActionIcon = (eventType: string) => {
    switch (eventType) {
      case "CREDITS_ADDED":
      case "CREDITS_REFUNDED":
        return <AddIcon sx={{ color: "success.main", fontSize: 16 }} />;
      case "CREDITS_USED":
      case "CREDITS_EXPIRED":
        return <RemoveIcon sx={{ color: "error.main", fontSize: 16 }} />;
      default:
        return <CreditIcon sx={{ color: "grey.500", fontSize: 16 }} />;
    }
  };

  const getActionChip = (transaction: CreditTransaction) => {
    const getLabel = (eventType: string) => {
      switch (eventType) {
        case "CREDITS_ADDED":
          return "Added";
        case "CREDITS_USED":
          return "Used";
        case "CREDITS_EXPIRED":
          return "Expired";
        case "CREDITS_REFUNDED":
          return "Refunded";
        default:
          return "Unknown";
      }
    };

    const getColor = (
      eventType: string,
    ): "success" | "error" | "warning" | "default" => {
      switch (eventType) {
        case "CREDITS_ADDED":
        case "CREDITS_REFUNDED":
          return "success";
        case "CREDITS_USED":
          return "error";
        case "CREDITS_EXPIRED":
          return "warning";
        default:
          return "default";
      }
    };

    return (
      <Chip
        icon={getActionIcon(transaction.eventType)}
        label={getLabel(transaction.eventType)}
        color={getColor(transaction.eventType)}
        size="small"
        variant="outlined"
      />
    );
  };

  const getCreditDisplay = (transaction: CreditTransaction) => {
    const getSignAndColor = (eventType: string) => {
      switch (eventType) {
        case "CREDITS_ADDED":
        case "CREDITS_REFUNDED":
          return { sign: "+", color: "success.main" };
        case "CREDITS_USED":
        case "CREDITS_EXPIRED":
          return { sign: "-", color: "error.main" };
        default:
          return { sign: "", color: "text.primary" };
      }
    };

    const { sign, color } = getSignAndColor(transaction.eventType);

    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        {getActionIcon(transaction.eventType)}
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            fontFamily: "monospace",
            color,
          }}
        >
          {sign}
          {transaction.credits}
        </Typography>
      </Box>
    );
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress size={24} />
        <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
          Loading credit history...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load credit history. Please try again later.
      </Alert>
    );
  }

  if (creditTransactions.length === 0) {
    return (
      <Box sx={{ textAlign: "center", p: 3 }}>
        <CreditIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
        <Typography variant="body2" color="text.secondary">
          No credit transactions found for this firm
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Credit additions and usage will appear here
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <TableContainer component={Paper} elevation={1}>
        <Table size="small" aria-label="credit history table">
          <TableHead>
            <TableRow sx={{ backgroundColor: "grey.50" }}>
              <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600 }}>
                Credits
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Invoice ID</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Batch ID</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {creditTransactions.map((transaction) => (
              <TableRow
                key={transaction.auditId}
                sx={{
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                  ...(transaction.success === false && {
                    backgroundColor: "error.light",
                    opacity: 0.7,
                  }),
                }}
              >
                <TableCell>
                  <Typography variant="body2">
                    {formatDate(transaction.timestamp)}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {getActionChip(transaction)}
                    {!transaction.success && (
                      <Chip
                        label="Failed"
                        color="error"
                        size="small"
                        variant="filled"
                      />
                    )}
                  </Box>
                </TableCell>

                <TableCell align="center">
                  {getCreditDisplay(transaction)}
                </TableCell>

                <TableCell>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {transaction.userEmail}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {transaction.userRole}
                    </Typography>
                  </Box>
                </TableCell>

                <TableCell>
                  <Typography
                    variant="body2"
                    sx={{
                      maxWidth: 200,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      fontFamily: "monospace",
                    }}
                    title={transaction.invoiceId || transaction.reason || "N/A"}
                  >
                    {transaction.invoiceId || transaction.reason || "N/A"}
                  </Typography>
                  {transaction.errorMessage && (
                    <Typography variant="caption" color="error.main">
                      Error: {transaction.errorMessage}
                    </Typography>
                  )}
                </TableCell>

                <TableCell>
                  {transaction.batchId && (
                    <Typography
                      variant="caption"
                      sx={{
                        fontFamily: "monospace",
                        backgroundColor: "grey.100",
                        px: 1,
                        py: 0.5,
                        borderRadius: 0.5,
                      }}
                    >
                      {transaction.batchId.substring(0, 8)}...
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {showPagination && creditTransactions.length >= limit && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <Pagination count={1} page={1} disabled sx={{ opacity: 0.5 }} />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ alignSelf: "center", ml: 2 }}
          >
            Pagination coming soon
          </Typography>
        </Box>
      )}
    </Box>
  );
}
